<script setup lang="ts">
/*
  tableSetting: {
			isHidePageSetting: false
			pageSize: 0,
			page: 0,
			tableClass: "agenda"
			rowClass: "",
			eventRowClass: "",
			oddRowClass:""
			sortColumnClass:"",
			sortColors: [],
			emptyMessage: "No data loaded",
			errorMessage: '',
			localStorage: true
		  }
  tableColumns, tableRows, tableTotalCount, restApi, sumRestApi, tableSums
	column: {
			  title: "ID",
			  id: "id",
			  isSortable: true,
              isTitleHtml: false,
			  rowType: false,
			  component: String
			}
	sums: {
		id: column.id,
		value: ""
		style: ""
		styleClass: ""
	}
 */

	import { ref, reactive, computed, onMounted, watch } from 'vue';

	import StringPrinter from './StringPrinter.vue';
	import DatePrinter from './DatePrinter.vue';
	import NumberPrinter from './NumberPrinter.vue';
	import HtmlPrinter from './HtmlPrinter.vue';
	import KeyPrinter from './KeyPrinter.vue';
	import BooleanPrinter from './BooleanPrinter.vue';
	import SummaryPrinter from './SummaryPrinter.vue';
	import SequencePrinter from './SequencePrinter.vue';
	import SelectPrinter from './StringPrinter.vue'; //PROXY :)
	import HeaderColumn from './HeaderColumn.vue';
		
	import { useBaseTable } from './BaseTable.js'


	const props = defineProps({
		tableColumns: Array,
		tableRows: Array,
		tableSums: Array,
		tableTotalCount: 0,
		restApi: String,
		sumRestApi: String,
		tableSetting: Object,
		disableClientStorage: Boolean
	});
	
	//setup
	if (props.tableSetting) {
		let pageStartIndex = props.tableSetting.page*props.tableSetting.pageSize;
		if (pageStartIndex>props.tableTotalCount) pageStartIndex=props.tableTotalCount;
		props.tableSetting.pageStartIndex = pageStartIndex;
	}
	else {
		//fake empty tableSetting
		props.tableSetting = {
			pageSize: 10,
			page: 0,
			pageStartIndex: 0,
			tableClass: "agenda",
			evenRowClass: "evenrow",
			oddRowClass:"oddrow",
			sortColumnClass: "sortedHeader",
			emptyMessage: "No data loaded"
		}
		props.tableTotalCount=0;
	}
	
	const {	setting,sortColumn,sortOrderAsc,pageSize,pageStartIndex,
			rows,columns,totalCount,header,errorMessage,
			preparedRows,pageEndIndex,isEmpty,isError,numberOfColumns,headerColumns,
			rowColumns,numberOfDataColumns,hasEditColumn,hasDeleteColumn,
			sortBy,rowClass,rowStyle,sortStyle,
			firstPage,lastPage,nextPage,prevPage,loadTable,refresh } = useBaseTable(props, initTableData);
	
	const sums = ref(props.tableSums);

	const hasSummary = computed(() => {
		return sums.value !== null && sums.value !== undefined;
	});
	
	function deleteRow(rowKey: string): void {
		let newRows = rows.value.filter(item => item.primaryKey !== rowKey);
		totalCount.value=totalCount.value - (rows.value.length-newRows.length);
		rows.value=newRows;
		if (hasSummary.value) {
			refreshSum();
		}
	}
	
	function initTableData(data): void {
		sums.value=data.tableSums;
	}
	
	function refreshSum(): Promise<boolean> {
		return fetch(props.sumRestApi)
			.then(response => response.json())
			.then(data => {
				//console.log("Loaded sums:"+JSON.stringify(data.tableSums));
				initTableData(data);
				return true;
			})
			.catch((error) => {
				console.log("error refresh sum:"+error);
				errorMessage.value = error;
				return false;
			});
	}
	
	function getSummary(columnId: string) {
		return sums.value.filter(sum => sum.id === columnId)[0];
	}
	
	function getComponent(componentName: string) 
	{
		switch(componentName) {
			case "StringPrinter": return StringPrinter;
			case "DatePrinter":  return DatePrinter;
			case "NumberPrinter": return NumberPrinter;
			case "HtmlPrinter": return HtmlPrinter;
			case "KeyPrinter": return KeyPrinter;
			case "BooleanPrinter": return BooleanPrinter;
			case "SummaryPrinter": return SummaryPrinter;
			case "SequencePrinter": return SequencePrinter;
			case "SelectPrinter": return StringPrinter; //PROXY :)
			default:
				console.log("Undefined component:"+componentName);
				errorMessage.value = "Undefined component:"+componentName;
				return null;
		}
	}
	
	defineExpose({refresh});

	onMounted(() => {
		if (Boolean(props.restApi)) { //not empty endpoint - use loading data
			loadTable();
		}
		//console.log("Mounted: rows:"+this.totalCount+" / pageSize:"+this.pageSize+" / startIndex:"+this.pageStartIndex+" / endIndex:"+this.pageEndIndex);
	});
	
</script>
<template>
	<slot></slot>
	<div v-if="isError" class="errors">
		<span class="perr">{{errorMessage}}</span>
	</div>
<div class="scroll-frame">
	<div class="scroll-frame-body">
		<table :class="setting.tableClass">
			<thead>
				<tr>
					<header-column v-for="column in headerColumns" v-bind="column" v-model="header[column.id]"
						:sortColumn="sortColumn" :sortOrderAsc="sortOrderAsc" :sortColumnClass="setting.sortColumnClass"
						@refresh="refresh" @sortBy="sortBy"/>
				</tr>
			</thead>
			<tbody v-if="isEmpty"><tr><td :colspan="numberOfColumns"><b>{{setting.emptyMessage}}</b></td></tr></tbody>
			<tbody v-else>
				<template v-for="(row, index) in preparedRows" :key="row.primaryKey">
					<tr :class="rowClass(row, index)" :style="rowStyle(row)">
						<template v-for="column in headerColumns">
							<template v-if="column.component==='SequencePrinter'">
								<SequencePrinter v-model="row[column.id].value" v-model.rowValue="row[column.id].rowValue" :sortStyle="sortStyle(column, index)"
										v-bind="row[column.id]" @emit-error="(error)=>errorMessage=error"/>
							</template>
							<template v-else>
								<component :is="getComponent(column.component)" v-bind="row[column.id]" :sortStyle="sortStyle(column, index)" 
								@emit-error="(error)=>errorMessage=error" @delete-row="deleteRow" />
							</template>
						</template>
					</tr>
					<template v-if="!row.emptyRowColumnsData">
						<tr v-for="column in rowColumns" :class="rowClass(row, index)" :style="rowStyle(row)">
							<td v-if="hasEditColumn"></td>
							<component :is="getComponent(column.component)" v-bind="row[column.id]" :colspan="numberOfDataColumns" />
							<td v-if="hasDeleteColumn"></td>
						</tr>
					</template>
				</template>
			  <tr v-if="hasSummary">
					<template v-for="column in headerColumns" class="summary">
						<summary-printer v-bind="getSummary(column.id)" class="summary"/>
					</template>
			  </tr>
			</tbody>
		</table>
	</div>
	<div class="scroll-frame-tail">	
		<div v-if="!setting.isHidePageSetting" class="tablepager">
			<div class="tablepager-content">
				<span class="pagercontrols">
					<span :class="{ disabledclick: pageStartIndex <= 0,  disabled: pageStartIndex <= 0, fi: true, activeclick: true}" style="width:22px" @click="firstPage()"></span>
					<span :class="{ disabledclick: pageStartIndex <= 0, disabled: pageStartIndex <= 0, fi: true, activeclick: true }" style="width:22px" @click="prevPage()"></span>
					<span :class="{ disabledclick: pageStartIndex >= totalCount-pageSize, disabled: pageStartIndex >= totalCount-pageSize, fi: true, activeclick: true }" style="width:22px" @click="nextPage()"></span>
					<span :class="{ disabledclick: pageStartIndex >= totalCount-pageSize,  disabled: pageStartIndex >= totalCount-pageSize, fi: true, activeclick: true }" style="width:22px" @click="lastPage()"></span>

					<b><span style="margin-left:4px" class="problemAgenda_from">{{pageStartIndex+(isEmpty?0:1)}}</span> - <span class="problemAgenda_to">{{pageEndIndex}}</span></b>
					<span style="margin-left:4px;opacity:0.7">({{totalCount}} záznamů)</span>
					<span style="margin-left:4px" v-if="!isEmpty">
						<select v-model.number="pageSize">
							<option value="10">10</option>
							<option value="20">20</option>
							<option value="50">50</option>
							<option value="100">100</option>
						</select>
					</span>
				</span>
			</div>
		</div>
	</div>
</div>
</template>
